import React, { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Rss, ChevronDown, ChevronRight,
} from "lucide-react";
import axios, { API, fmt, fmtTime, fmtPct, pctColor } from "./commoditiesLib";
import { api } from "../../lib/api";
import { hasOpenRow, LIVE_POLL_FAST_MS } from "../../lib/livePoll";
import { useAuth } from "../../auth/AuthContext";
import KiteConsole from "./KiteConsole";
import { MyLiveTrades } from "../../components/MyLiveTrades";
import { TodayLivePnl } from "../../components/TodayLivePnl";

// 2026-08-22 — "Build live commodity order-placement into StoxTrade" +
// "add Option to enable/disable for commodity trading also" (Settings page
// toggle lives in UserPages.jsx; this screen is the platform-wide kill-
// switch + order-shape visibility, mirroring CandleBreakout.jsx's Live/
// Pause button and confirm-dialog exactly).
//
// Every live attempt below is a document that already existed as a paper
// trade (commodity_paper_trades, live_trade=True) — there is no separate
// "live orders" collection. A live order is ONLY ever attempted off a REAL
// Upstox quote (quote_source="live"), never a Black-76 estimate — that gate
// is hard-coded in commodity_autopilot._maybe_place_live_order and cannot
// be configured away from here.
//
// 2026-09-13 — chat: "Commodities:...Execution (make it Live Orders) and
// should be user specific. Admin should be able to see for all users (one
// table per user)." This page is no longer ADMIN-only (see App.js's
// /commodities/execution route): every active user can open it and see
// their own live-order attempts (via GET /commodities/live-orders, which
// scopes per-viewer — see commodity_autopilot.list_live_orders). The
// platform-wide kill-switches (Live/Kite toggles) and Order Shape summary
// below stay ADMIN-only content within this same page — they're global
// settings, not something a regular user should see or control.

const STATUS_COLOR = {
  confirmed: "var(--bull)",
  rejected: "var(--bear)",
  unconfirmed: "var(--text-muted-2)",
  // 2026-09-16 — skip-and-reconcile path (commodity_autopilot.confirm_kite_order):
  // the position was already flat at the broker (e.g. exited manually in the
  // Kite app) — "reconciled" means we recovered the real exit price from
  // Kite's trade history, "closed_externally_unconfirmed" means we couldn't.
  reconciled: "var(--bull)",
  closed_externally_unconfirmed: "var(--text-muted-2)",
};

const DiagBlock = ({ label, value }) => {
  if (value == null) return null;
  const text = typeof value === "string" ? value : JSON.stringify(value, null, 2);
  return (
    <div className="space-y-1">
      <div className="text-[9px] uppercase tracking-[0.15em] text-muted-3 font-mono-data">{label}</div>
      <pre className="text-[10px] font-mono-data whitespace-pre-wrap break-all bg-black/20 border border-grid/60 p-2 max-h-64 overflow-y-auto">
        {text}
      </pre>
    </div>
  );
};

// 2026-09-16 — chat: "Provide Exit Option in LiveOrders page." Only a
// KITE-broker, still-OPEN position with a real BUY fill can be exited here
// — Upstox live trades have no automated SELL capability at all (see
// manual_exit's live_note above), so the button is hidden rather than
// shown-then-always-failing for those. Admin-gated same as the P&L page's
// Exit button, since the backend endpoint requires admin.
//
// 2026-09-16 — chat: "Deployed..PNL is still showing as Open" led to fixing
// manual_exit_live_kite's own idempotency guard to allow retrying after a
// FAILED prior exit attempt (see commodity_autopilot.py) — but this
// function originally ALSO required `!t.live_exit_status`, which hides the
// button the moment ANY attempt (including a rejected one) stamps
// live_exit_status, defeating that exact retry fix from the UI side: once a
// SELL got rejected, the button vanished and there was no way to click Exit
// again at all. `status === "OPEN"` is already the authoritative signal —
// the backend only ever flips a trade to CLOSED on a genuinely resolved
// exit (real fill, or a confirmed-flat reconciliation) — so a leftover
// live_exit_status from a past failure must NOT hide this button.
// 2026-09-28 — also true when the PAPER trade is already CLOSED but the REAL
// position is still open (exit SELL failed/unfilled/deferred) — backend
// manual_exit_live_kite now accepts that case.
const _REAL_EXIT_DONE = new Set(["confirmed", "reconciled", "reconciled_manual_exit", "closed_externally_unconfirmed"]);
const realPositionOpen = (t) =>
  t.live_broker === "KITE" && t.live_avg_fill_price != null &&
  t.live_exit_avg_fill_price == null && !_REAL_EXIT_DONE.has(t.live_exit_status);
const canExitLive = (t) =>
  (t.status === "OPEN" && t.live_broker === "KITE" && t.live_avg_fill_price != null) ||
  realPositionOpen(t);

const LiveAttemptRow = ({ t, isAdmin, onExitLive }) => {
  const [open, setOpen] = useState(false);
  const color = STATUS_COLOR[t.live_status] || "var(--text-muted-2)";
  return (
    <Fragment>
      <tr className="border-b border-grid/40 hover:bg-white/[0.02]" data-testid="commodities-live-attempt-row">
        <td className="px-3 py-2.5">
          <button onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-2">
            <span className="text-muted-3">{open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</span>
            <span className="font-semibold">{t.underlying}</span>
          </button>
        </td>
        <td className="px-3 py-2.5 text-muted-2">{t.option_type} {fmt(t.strike, 0)}</td>
        <td className="px-3 py-2.5 text-right text-muted-2">{t.live_qty_lots ?? "—"}</td>
        <td className="px-3 py-2.5 font-mono-data text-[10px] text-muted-3">
          {t.live_broker || "UPSTOX"}
        </td>
        <td className="px-3 py-2.5 font-mono-data text-[11px]" style={{ color }}>
          {(t.live_status || "—").toUpperCase()}
        </td>
        <td className="px-3 py-2.5 text-muted-2 font-mono-data text-[11px]">{t.live_order_id || t.live_tradingsymbol || "—"}</td>
        <td className="px-3 py-2.5 text-muted-3 font-mono-data text-[10px]">{fmtTime(t.entry_time)}</td>
        <td className="px-3 py-2.5 text-right">
          {isAdmin && canExitLive(t) ? (
            <button
              onClick={() => onExitLive(t)}
              data-testid={`commodities-live-exit-${t.id}`}
              className="px-2 py-1 border text-[10px] uppercase tracking-wider font-mono-data"
              style={{ color: "var(--bear)", borderColor: "var(--bear)" }}
            >
              Exit
            </button>
          ) : (t.status === "OPEN" || realPositionOpen(t)) ? (
            <span className="text-[10px] text-muted-3 font-mono-data uppercase">
              {t.live_broker === "KITE" ? (t.live_exit_status ? t.live_exit_status.toUpperCase() : "Open") : "—"}
            </span>
          ) : (
            <span className="text-[10px] text-muted-3 font-mono-data uppercase">Closed</span>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-b border-grid/40" style={{ background: "rgba(255,255,255,0.015)" }}>
          <td colSpan={8} className="px-3 py-3">
            <div className="space-y-3 pl-9">
              {t.live_error && (
                <div className="text-[11px] font-mono-data">
                  <span className="text-muted-3">Error code </span>
                  <span style={{ color: "var(--bear)" }}>{t.live_error}</span>
                  {t.live_error_message && (
                    <span className="text-muted-2"> — {t.live_error_message}</span>
                  )}
                </div>
              )}
              {t.live_broker_status && (
                <div className="text-[11px] font-mono-data text-muted-2">
                  Broker status: {t.live_broker_status}
                  {t.live_filled_qty != null && ` · Filled qty ${t.live_filled_qty}`}
                  {t.live_avg_fill_price != null && ` · Avg fill ${fmt(t.live_avg_fill_price)}`}
                </div>
              )}
              {/* 2026-09-15 — real entry/exit fill price + real P&L, stamped by
                  kite_service.confirm_order_filled polling Kite's own order
                  history after a BUY/SELL confirms (commodity_autopilot.py).
                  Shown separately from the live_broker_status block above
                  since that one's Upstox-only naming (live_broker_status /
                  live_filled_qty aren't set on the Kite path). Chat: "Minor
                  mismatch between order execution and paper trading." */}
              {(t.live_avg_fill_price != null || t.live_exit_status) && (
                <div className="text-[11px] font-mono-data text-muted-2 space-y-0.5">
                  {t.live_avg_fill_price != null && (
                    <div>Real entry fill: <span className="text-muted-1">{fmt(t.live_avg_fill_price)}</span></div>
                  )}
                  {t.live_exit_status && (
                    <div>
                      Exit: <span style={{ color: STATUS_COLOR[t.live_exit_status] || "var(--text-muted-2)" }}>
                        {t.live_exit_status.toUpperCase()}
                      </span>
                      {t.live_exit_avg_fill_price != null && ` · Real exit fill ${fmt(t.live_exit_avg_fill_price)}`}
                      {t.live_exit_error_message && ` — ${t.live_exit_error_message}`}
                      {t.live_exit_message && ` — ${t.live_exit_message}`}
                    </div>
                  )}
                  {t.live_pnl_pct != null && (
                    <div>Real P&amp;L: <span style={{ color: pctColor(t.live_pnl_pct) }}>{fmtPct(t.live_pnl_pct)}</span></div>
                  )}
                </div>
              )}
              <DiagBlock label={`Full diagnostics (request/response — safe to paste into ${t.live_broker === "KITE" ? "a Zerodha/Kite" : "an Upstox"} support ticket, never includes the access token)`}
                         value={t.live_diagnostics} />
              {!t.live_diagnostics && !t.live_error && (
                <div className="text-[11px] text-muted-3 font-mono-data">No diagnostics recorded for this attempt.</div>
              )}
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
};

const CommoditiesExecution = () => {
  const { isAdmin } = useAuth();
  const [liveEnabled, setLiveEnabled] = useState(null);
  const [liveBusy, setLiveBusy] = useState(false);
  const [overview, setOverviewData] = useState(null);
  const [liveCfg, setLiveCfg] = useState(null);
  const [attempts, setAttempts] = useState([]);
  // 2026-09-13 — grouped one-table-per-user view; only ever populated for
  // an ADMIN viewer (see commodity_autopilot.list_live_orders). null for a
  // regular user, who just gets `attempts` scoped to their own rows.
  const [byUser, setByUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 2026-09-10 — Kite Connect (CRUDEOIL, Phase 1) — an ENTIRELY separate
  // kill-switch/config from the Upstox live-trading block above. See
  // commodity_autopilot.py's KITE EXECUTION section.
  const [kiteEnabled, setKiteEnabled] = useState(null);
  const [kiteCfg, setKiteCfg] = useState(null);

  // 2026-10-16 — orders feed polls 10 s while any attempt is OPEN, else 30 s
  // (lib/livePoll.js); admin status/config calls stay on the 30 s load below.
  const loadOrders = useCallback(async () => {
    // 2026-09-13 — user-specific Live Orders feed, viewer-scoped
    // server-side (own rows only for a regular user; every attempt +
    // `by_user` grouping for an ADMIN viewer). Everyone can call this now.
    const liveOrdersRes = await axios.get(`${API}/commodities/live-orders`)
      .catch(() => ({ data: { orders: [], by_user: null } }));
    setAttempts(liveOrdersRes.data?.orders || []);
    setByUser(liveOrdersRes.data?.by_user || null);
  }, []);

  const load = useCallback(async () => {
    try {
      await loadOrders();

      if (isAdmin) {
        const [statusRes, overviewRes, cfgRes, kiteExecRes, kiteCfgRes] = await Promise.all([
          axios.get(`${API}/commodities/live-status`).catch(() => ({ data: { enabled: false } })),
          axios.get(`${API}/commodities/overview`).catch(() => ({ data: null })),
          api.get(`/admin/config/commodity_live_trading`).catch(() => ({ data: null })),
          axios.get(`${API}/commodities/kite/execution-status`).catch(() => ({ data: { enabled: false } })),
          api.get(`/admin/config/kite_execution`).catch(() => ({ data: null })),
        ]);
        setLiveEnabled(!!statusRes.data?.enabled);
        setOverviewData(overviewRes.data);
        setLiveCfg(cfgRes.data);
        setKiteEnabled(!!kiteExecRes.data?.enabled);
        setKiteCfg(kiteCfgRes.data);
      }
    } finally {
      setLoading(false);
    }
  }, [isAdmin, loadOrders]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const anyOpen = hasOpenRow(attempts);
  useEffect(() => {
    if (!anyOpen) return undefined;
    const t = setInterval(() => { if (!document.hidden) loadOrders(); }, LIVE_POLL_FAST_MS);
    return () => clearInterval(t);
  }, [anyOpen, loadOrders]);

  const onToggleLive = async () => {
    const next = !liveEnabled;
    const verb = next ? "RESUME" : "PAUSE";
    if (!window.confirm(
      `${verb} Commodities LIVE trading?\n\n` +
      (next
        ? "New auto-entries with a REAL Upstox quote will place real MCX BUY orders again.\n" +
          "Estimated (Black-76) signals will still be paper-only — that gate is not configurable."
        : "New auto-entries will be SKIPPED for live orders (paper-mode auto-entry continues normally).\n" +
          "This does not touch any already-open live position — commodity live trades have no automatic SELL leg; exit manually via Upstox or the Paper Trading screen.")
    )) return;
    setLiveBusy(true);
    try {
      await axios.post(`${API}/commodities/live-status`, { enabled: next });
      setLiveEnabled(next);
      toast.success(next ? "Commodities LIVE trading RESUMED" : "Commodities LIVE trading PAUSED");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update live-trading flag");
    } finally {
      setLiveBusy(false);
    }
  };

  // 2026-09-16 — chat: "Provide Exit Option in LiveOrders page." Places a
  // REAL Kite SELL immediately (see commodity_autopilot.manual_exit_live_kite);
  // same endpoint + confirm wording as the P&L page's Exit button, so the
  // two screens can't drift into different behavior for the same action.
  const handleExitLive = async (t) => {
    if (!window.confirm(
      `Place a REAL Kite SELL now for ${t.underlying} ${t.option_type} ${t.strike}? ` +
      `This closes the actual broker position, not a simulation.`
    )) return;
    try {
      const { data: res } = await axios.post(`${API}/commodities/paper-trades/manual-exit-live`, { trade_id: t.id });
      const orderId = res?.kite_result?.order_id;
      toast.success(`${t.underlying} exit order placed${orderId ? ` (Kite order ${orderId})` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Live exit failed");
    }
  };

  return (
    <section className="space-y-4" data-testid="commodities-execution-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
            Commodities · MCX
          </div>
          <h2 className="font-heading text-2xl flex items-center gap-2">
            <Rss size={20} /> Live Orders
          </h2>
          <p className="text-xs text-muted-2 mt-1 max-w-3xl" data-testid="commodities-execution-description">
            {isAdmin ? (
              <>
                Real MCX orders go through Zerodha Kite Connect — Upstox can't place real MCX
                orders at all, so its live-trading toggle and order-shape controls are hidden here
                (chat: "Paused - click to Resume button...it is only upstox? Remove/hide as Upstox
                isn't working"). Kite (CRUDEOIL/SILVERM) is confirm-gated — it only fires when you
                explicitly tap Confirm on the Overview screen's pending card. Shows confirmed/
                rejected/unconfirmed status and full request/response diagnostics below — safe to
                paste into a support ticket (never includes the access token). Older attempts in the
                table below may still show broker UPSTOX from before this change — kept for history.
              </>
            ) : (
              "Your live commodity order attempts — confirmed/rejected/unconfirmed status and full request/response diagnostics for each."
            )}
          </p>
        </div>
      </div>

      {/* 2026-09-13 — platform-wide kill-switches + config summary: ADMIN
          only. A regular user can now open this page (chat: "should be
          user specific") but these aren't per-user settings — they're the
          single shared broker connection's global controls.

          2026-09-16 — the Upstox LIVE toggle button + "Order Shape & Daily
          Cap" card (both Upstox-only — driven by commodity_live_trading
          config) were removed from view here (chat: "Paused - click to
          Resume button...it is only upstox? Remove/hide as Upstox isn't
          working"). Upstox genuinely cannot place real MCX orders (see the
          Kite card's own description below, which already said as much) —
          the button was controlling a path that never worked, which is
          confusing on its own. The underlying kill-switch/state
          (liveEnabled, onToggleLive, /commodities/live-status) is left
          wired in the code untouched in case Upstox MCX support ever
          becomes real and this needs to come back — just not rendered. */}
      {/* 2026-10-15 — chat: "We might not need this...I can disable from Settings page".
          The Kite Execution card is gone; its on/off is now Settings → Commodity Live
          Trading (owner = master switch). One status line stays for at-a-glance. */}
      {isAdmin && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[11px] font-mono-data border border-grid px-4 py-2"
             data-testid="commodities-kite-status-line">
          <span style={{ color: kiteEnabled ? "var(--bull)" : "var(--bear)" }}>
            Real MCX orders: {kiteEnabled ? "ON" : "OFF"}
          </span>
          <span className="text-muted-2">
            Orders today <span className="text-muted-1">{overview?.kite_orders_today ?? "—"} / {kiteCfg?.values?.max_orders_per_day ?? "—"}</span>
          </span>
          <span className="text-muted-2">
            Lots: {Object.entries(kiteCfg?.values?.quantity_lots || {}).map(([u, q]) => (
              <span key={u} className="text-muted-1 mr-2">{u}={q}</span>
            ))}
          </span>
          <a href="/settings" className="underline" style={{ color: "var(--bull)" }}>Turn on/off in Settings → Commodity Live Trading</a>
        </div>
      )}

      {/* 2026-10-30 — today's realized P&L from real Kite fills (all accounts the
          viewer can see), like the Stocks Live Orders card. */}
      <TodayLivePnl market="commodities" testid="commodities-today-pnl" />

      {/* 2026-10-05 — per-account console (positions / orders / manual
          Buy-Sell / Cancel-Modify-Exit) for the primary + linked family
          Kite accounts; renders nothing for a user with no account. */}
      <KiteConsole />

      {/* 2026-10-13 — a linked (family) account's own real commodity trades,
          placed by the autopilot mirror (kite_mirror.py). */}
      {!isAdmin && <MyLiveTrades market="commodities" title="My live commodity trades" />}

      {/* Live-order attempts — 2026-09-13: an ADMIN viewer with `byUser`
          gets one table per user (chat: "Admin should be able to see for
          all users (one table per user)"); everyone else (and an admin
          before `byUser` loads) gets the single flat table this always
          was, just scoped to their own rows server-side now. */}
      {isAdmin && byUser ? (
        <div className="space-y-4">
          {byUser.length === 0 ? (
            <div className="border border-grid p-8 text-center text-xs text-muted-2 font-mono-data">
              No live-order attempts yet within the retention window.
            </div>
          ) : (
            byUser.map((grp) => (
              <div key={grp.user_id || "unknown"} className="border border-grid overflow-x-auto" data-testid={`commodities-live-orders-user-group-${grp.user_id || "unknown"}`}>
                <div className="px-3 py-2 border-b border-grid text-xs uppercase tracking-[0.2em] font-mono-data text-muted-2 flex items-center gap-2">
                  {grp.user_name}
                  <span className="text-muted-3 normal-case tracking-normal">· {grp.orders.length} attempt{grp.orders.length === 1 ? "" : "s"}</span>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-3 uppercase tracking-wider text-[10px] font-mono-data border-b border-grid">
                      <th className="px-3 py-2">Underlying</th>
                      <th className="px-3 py-2">Leg</th>
                      <th className="px-3 py-2 text-right">Qty (lots)</th>
                      <th className="px-3 py-2">Broker</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Order ID</th>
                      <th className="px-3 py-2">Entered</th>
                      <th className="px-3 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grp.orders.map((t) => (
                      <LiveAttemptRow key={t.id} t={t} isAdmin={isAdmin} onExitLive={handleExitLive} />
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </div>
      ) : (
      <div className="border border-grid overflow-x-auto">
        <div className="px-3 py-2 border-b border-grid text-xs uppercase tracking-[0.2em] font-mono-data text-muted-2">
          {isAdmin ? "Recent Live-Order Attempts" : "Your Live-Order Attempts"}
        </div>
        {loading ? (
          <div className="p-6 text-center text-xs text-muted-2 font-mono-data">Loading…</div>
        ) : attempts.length === 0 ? (
          <div className="p-8 text-center text-xs text-muted-2 font-mono-data">
            No live-order attempts yet within the retention window.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-3 uppercase tracking-wider text-[10px] font-mono-data border-b border-grid">
                <th className="px-3 py-2">Underlying</th>
                <th className="px-3 py-2">Leg</th>
                <th className="px-3 py-2 text-right">Qty (lots)</th>
                <th className="px-3 py-2">Broker</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Order ID</th>
                <th className="px-3 py-2">Entered</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {attempts.map((t) => (
                <LiveAttemptRow key={t.id} t={t} isAdmin={isAdmin} onExitLive={handleExitLive} />
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}
    </section>
  );
};

export default CommoditiesExecution;
